import { useCallback } from 'react';
import useSWR from 'swr';

import { AgentApiRequestHelper } from '../agent-api-request-helper';
import { DOCUMENT_READER_FINISHED_RUN_STATES } from './constants';
import {
  DocumentReaderReExtractState,
  DocumentReaderWriteBackState,
  type IDocumentReaderRunResponse,
  type IUseDocumentReaderRunResult,
} from './types';

export function useDocumentReaderRun(
  runId: string | null
): IUseDocumentReaderRunResult {
  const { data, isLoading, error, mutate } = useSWR(
    runId
      ? `agent-platform/document-reader/runs/${encodeURIComponent(runId)}`
      : null,
    (path: string) =>
      AgentApiRequestHelper.read<IDocumentReaderRunResponse>(path),
    {
      // A run that has ended never changes again, so asking stops there, unless its write-back or re-read is still going.
      refreshInterval: (latest?: IDocumentReaderRunResponse) => {
        const writeBackState = latest?.run.writeBack?.state;
        if (
          writeBackState === DocumentReaderWriteBackState.QUEUED ||
          writeBackState === DocumentReaderWriteBackState.RUNNING ||
          latest?.run.reExtract?.state === DocumentReaderReExtractState.RUNNING
        )
          return 5000;
        return latest &&
          DOCUMENT_READER_FINISHED_RUN_STATES.includes(latest.run.state)
          ? 0
          : 5000;
      },
      revalidateOnFocus: false,
    }
  );

  const refreshRun = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return { run: data?.run ?? null, isLoading, error, refreshRun };
}
